/**
 * Skills and tools of a rated answer, beside those of its whole conversation
 * (Insights → Feedback review window). The backend decides what each list
 * holds from the records the turns acted on (feedback_capabilities): skills
 * in force when the answer was written, activated by it, or always on; tool
 * calls counted from the tool log, or what the answer recorded where calls
 * are not logged. Tools wear the words the status line showed while they ran.
 */

import { useLanguage } from '@/hooks/useLanguage';
import { bidiIsolate } from '@/utils/bidi';
import { formatSkillName } from '@modules/conversations/components/SkillBadges';
import type { TFunc } from '@/utils/tfunc';
import type { FeedbackCapabilities, UsedSkill, UsedTool } from '../../types/feedback';

/** Status-line labels that name what they read ({{filename}}, {{title}}):
 *  without the call's arguments, the review says it in general. */
const GENERAL_LABELS = new Set(['readingDocument', 'artifact_drafting']);

function humanize(tool: string): string {
  const words = tool.replace(/^[a-z0-9]+__/i, '').replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : tool;
}

/** The words the person saw while the tool ran, without the trailing "…". */
export function toolLabel(t: TFunc, tool: UsedTool): string {
  if (GENERAL_LABELS.has(tool.key)) return t(`insights.feedback.used.toolLabel.${tool.key}`);
  const params: Record<string, string> = { ...tool.params };
  if (tool.key === 'usingTool') params.tool = humanize(tool.params.tool || tool.tools[0] || '');
  for (const k of Object.keys(params)) params[k] = bidiIsolate(params[k]);
  return t(`chat.progress.${tool.key}`, params).replace(/\s*(…|\.\.\.)\s*$/, '');
}

function SkillChip({ skill, conversation }: { skill: UsedSkill; conversation?: boolean }) {
  const { t } = useLanguage();
  const off = conversation && skill.in_force === false;
  const note = conversation
    ? (skill.how === 'always_on' ? t('insights.feedback.used.alwaysOn') : off ? t('insights.feedback.used.noLongerInForce') : null)
    : t(`insights.feedback.used.how.${skill.how}`);
  return (
    <span className={`cx-pill fbw__chip${off ? ' is-off' : ''}`} data-testid="feedback-skill-chip">
      <bdi dir="auto">{formatSkillName(t, skill.name)}</bdi>
      {note && <span className="fbw__chip-note">{note}</span>}
    </span>
  );
}

function ToolChip({ tool }: { tool: UsedTool }) {
  const { t } = useLanguage();
  return (
    <span className="cx-pill fbw__chip" title={tool.tools.length ? tool.tools.join(', ') : undefined}
      data-testid="feedback-tool-chip">
      <span>{toolLabel(t, tool)}</span>
      {tool.count !== null && tool.count > 1 && (
        <span className="fbw__chip-count">{t('insights.feedback.used.times', { count: tool.count })}</span>
      )}
    </span>
  );
}

function Row({ label, empty, children, testId }: {
  label: string; empty: string; children: React.ReactNode[]; testId: string;
}) {
  return (
    <div className="fbw__used-row" data-testid={testId}>
      <span className="fbw__used-label">{label}</span>
      <span className="fbw__pills">
        {children.length ? children : <span className="fbw__muted">{empty}</span>}
      </span>
    </div>
  );
}

export function UsedCapabilities({ capabilities }: { capabilities: FeedbackCapabilities }) {
  const { t } = useLanguage();
  const { skills, tools } = capabilities;
  return (
    <>
      <section className="fbw__section" data-testid="feedback-window-skills">
        <h3 className="fbw__label">{t('insights.feedback.used.skills')}</h3>
        <p className="fbw__muted fbw__used-hint">{t('insights.feedback.used.skillsHint')}</p>
        <Row label={t('insights.feedback.used.inAnswer')} empty={t('insights.feedback.used.noSkillAnswer')}
          testId="feedback-skills-answer">
          {skills.answer.map((s) => <SkillChip key={s.name} skill={s} />)}
        </Row>
        <Row label={t('insights.feedback.used.inConversation')} empty={t('insights.feedback.used.noSkillConversation')}
          testId="feedback-skills-conversation">
          {skills.conversation.map((s) => <SkillChip key={s.name} skill={s} conversation />)}
        </Row>
      </section>

      <section className="fbw__section" data-testid="feedback-window-tools">
        <h3 className="fbw__label">{t('insights.feedback.used.tools')}</h3>
        <p className="fbw__muted fbw__used-hint">
          {tools.source === 'log' ? t('insights.feedback.used.toolsHintLog') : t('insights.feedback.used.toolsHintRecorded')}
        </p>
        <Row label={t('insights.feedback.used.inAnswer')} empty={t('insights.feedback.used.noToolAnswer')}
          testId="feedback-tools-answer">
          {tools.answer.map((x) => <ToolChip key={`${x.key}:${JSON.stringify(x.params)}`} tool={x} />)}
        </Row>
        <Row label={t('insights.feedback.used.inConversation')} empty={t('insights.feedback.used.noToolConversation')}
          testId="feedback-tools-conversation">
          {tools.conversation.map((x) => <ToolChip key={`${x.key}:${JSON.stringify(x.params)}`} tool={x} />)}
        </Row>
      </section>
    </>
  );
}
